import { HttpClient, HttpEventType } from '@angular/common/http';
import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Path, JSONRPCErrorCode, Tree, Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';

import { CmsServerService, MediaMediaFileItem } from 'src/app/services/jsonrpc/cms-server.service';
import { CmsService } from 'src/app/services/cms.service';
import { ImageCacheService } from 'src/app/services/image-cache.service';

import { FileUploadComponent } from './../file-upload/file-upload.component';

export enum FileType {
  Image = 'image',
  Video = 'video',
  Audio = 'audio',
  Document = 'document',
  Folder = 'folder',
  Other = 'other'
}

const fileIcons: any = {
  image: 'file-image',
  audio: 'file-audio',
  video: 'file-video',
  document: 'file',
  other: 'file',
  folder: 'folder'
};

export class File {
  public name: string;

  public get icon(): string {
    return fileIcons[this.type];
  }

  public get type(): FileType {
    switch (this._type) {
      case 'image':
        return FileType.Image;
      case 'video':
        return FileType.Video;
      case 'podcast':
        return FileType.Audio;
      case 'folder':
        return FileType.Folder;
      case 'document':
        return FileType.Document;
      default:
        return FileType.Other;
    }
  }

  public get basename(): string {
    const index = this.name.lastIndexOf('.');
    return index != -1 ? this.name.substr(0, index) : this.name;
  }

  public get isFolder(): boolean {
    return this.type == FileType.Folder;
  }

  constructor(public uuid: string, public node: string, public path: Path, public _type: string) {
    this.name = path.getLast();
  }
}

@Component({
  selector: 'app-media-explorer',
  templateUrl: './media-explorer.component.html',
  styleUrls: ['./media-explorer.component.scss']
})
export class MediaExplorerComponent {
  @ViewChild(FileUploadComponent) private fileUpload: FileUploadComponent;

  @Output() public pathChange = new EventEmitter<Path>();

  @Input() public select: File[] = [];
  @Output() public selectChange = new EventEmitter<File[]>();

  @Input() public tree: Tree;
  @Input() public size = 100;
  @Input() public multi = false;

  public end = this.size;
  public typesFilter = [];

  public loading = false;
  public uploadProgress = {};
  public uploading = false;
  public reference = false;

  public fileTypes = {
    'application/pdf': 'document',
    'image/png': 'image',
    'application/msword': 'document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
    'application/vnd.ms-excel': 'document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'document',
    'application/vnd.ms-powerpoint': 'document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'document',
    'application/vnd.openxmlformats-officedocument.presentationml.template': 'document',
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow': 'document',
    'image/jpg': 'image',
    'image/jpeg': 'image',
    'image/gif': 'image',
    'audio/mpeg': 'podcast',
    'video/mp4': 'video'
  };

  private _files: File[] = [];
  private _path: Path;

  @Input() public set path(v: Path) {
    if (this._path != v) {
      this._path = v;
      void this.fetch();
    }
  }
  public get path(): Path {
    return this._path;
  }

  public get files(): File[] {
    return this._files.filter(f => f.name[0] !== '.').slice(0, this.end);
  }

  public get canUpload(): boolean {
    return this.hasPermission(PermissionVerb.Write) && !this.reference;
  }

  public get canDelete(): boolean {
    return this.hasPermission(PermissionVerb.Delete) && !this.reference;
  }

  public get selectedText() {
    if (this.select.length == 1) {
      return this.select[0].name;
    }
    return `${this.select.length} Files`;
  }

  constructor(public server: CmsServerService, public image: ImageCacheService, private cms: CmsService, private http: HttpClient) {}

  public isSelected(f: File): boolean {
    return this.select.findIndex(s => s.path == f.path) != -1;
  }

  public delete(files: File[]) {
    return Promise.all(
      files.map(f =>
        this.server
          .mediaDelete({
            path: f.path.uri()
          })
          .then(() => this.fetch())
      )
    );
  }

  public onFile(f: File) {
    if (f.isFolder) {
      this.pathChange.emit(this._path.addLast(f.name));
      return;
    }

    if (this.multi) {
      const index = this.select.findIndex(s => s.path == f.path);
      if (index == -1) {
        this.select.push(f);
      } else {
        this.select.splice(index, 1);
      }
    } else {
      this.select = [f];
    }
    this.selectChange.emit(this.select);
  }

  public onScroll() {
    this.end += this.size;
    if (this.end > this._files.length) {
      this.end = this._files.length;
    }
  }

  public imageUrl(f: File) {
    if (f.type != FileType.Image) {
      return Promise.resolve(null);
    }
    return this.image.url(f.path).then(url => {
      return {
        'background-image': `url('${url}')`
      };
    });
  }

  public async download(files: File[]) {
    for (const f of files) {
      const url = await this.image.url(f.path);
      window.open(url, '_blank');
    }
  }

  public updateFilters(ev) {
    this.typesFilter = ev;
  }

  public cleanFileUpload() {
    this.uploadProgress = {};
    this.uploading = false;
    this.fileUpload.reset();
  }

  public upload(files: { title: string; file: File }[]) {
    this.uploading = true;

    const promises = [];
    for (const f of files) {
      promises.push(this.uploadFile(f.file, f.title || f.file.name));
    }
    return Promise.all(promises)
      .then(files => {
        this.cleanFileUpload();
        return files;
      })
      .then(() => this.fetch())
      .catch(() => this.cleanFileUpload());
  }

  private hasPermission(verb: PermissionVerb) {
    if (!this.path) {
      return false;
    }
    const node = this.tree.get(this.path);
    if (!node) {
      return false;
    }
    return this.cms.hasAccess(new Permission(new PermissionSubject('node', 'media'), verb), node.uuid);
  }

  private uploadFile(file, name) {
    const t: FileType = this.fileTypes[file.type];

    return this.server
      .mediaUpload({
        path: this._path.addLast(name).uri(),
        type: t
      })
      .then(
        res =>
          new Promise((resolve, reject) => {
            this.http
              .put(res.url, file, {
                reportProgress: true,
                observe: 'events'
              })
              .subscribe(
                ev => {
                  if (ev.type == HttpEventType.Response) {
                    return setTimeout(() => resolve(file), 1500);
                  } else if (ev.type == HttpEventType.UploadProgress) {
                    this.uploadProgress[name] = Math.round((ev.loaded / ev.total) * 100);
                  }
                },
                err => reject(err)
              );
          })
      );
  }

  private fetch() {
    this.loading = true;

    const node = this.tree.get(this._path);
    const ref = this.cms.resolveReferenceRecursive(node);

    return this.server
      .mediaList({
        path: this._path.uri(),
        recursive: false
      })
      .catch(err => {
        if (err.code != JSONRPCErrorCode.NOT_FOUND) {
          throw err;
        }
        console.warn('media path not found');
        return [] as MediaMediaFileItem[];
      })
      .then(list => {
        const files = list.map(f => new File(f.uuid, node.uuid, Path.parse(f.path), f.type));

        this.end = this.size;
        this._files = files;
        this.select = [];
        this.reference = ref != null;
      })
      .finally(() => (this.loading = false));
  }

  public isDocument(type: string) {
    return type === 'document';
  }
}
